import React, { useMemo, useState } from 'react';
import { formService } from '../services/formService';
import { storageService } from '../services/storageService';
import { resolveFieldOptions } from '../utils/formOptions';
import { optimiseImageFile } from '../utils/imageOptimisation';

const MAX_RESPONSE_FILE_BYTES = 5 * 1024 * 1024;
const MAX_RESPONSE_IMAGE_BYTES = 300 * 1024;

const normaliseUrl = (value = '') => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';
  if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
  return `https://${trimmedValue}`;
};

const formatFileSize = (bytes = 0) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

function ShareFormRenderer({ asset = null, form: formOverride = null, link, className = 'mt-6' }) {
  const form = formOverride || asset?.form || link?.form;
  const fields = useMemo(() => form?.fields || [], [form]);
  const [answers, setAnswers] = useState({});
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!form?.enabled || !fields.length) return null;

  const updateAnswer = (fieldId, value) => {
    setAnswers((current) => ({ ...current, [fieldId]: value }));
  };

  const validate = () => {
    const missingField = fields.find((field) => {
      if (field.type === 'button') return false;
      if (!field.required) return false;
      const value = answers[field.id];
      if (field.type === 'checkbox') return !value;
      if (field.type === 'file') return !value?.file && !value?.downloadUrl;
      if (field.type === 'multiselect') return !Array.isArray(value) || !value.length;
      return !String(value || '').trim();
    });

    if (missingField) {
      return `${missingField.label} is required.`;
    }

    return '';
  };

  const prepareFileAnswers = async () => {
    const preparedAnswers = { ...answers };

    for (const field of fields) {
      if (field.type !== 'file') continue;
      const answer = preparedAnswers[field.id];
      if (!answer?.file) continue;

      if (answer.file.size > MAX_RESPONSE_FILE_BYTES) {
        throw new Error(`${field.label} must be ${formatFileSize(MAX_RESPONSE_FILE_BYTES)} or smaller.`);
      }

      const preparedFile = await optimiseImageFile(answer.file, MAX_RESPONSE_IMAGE_BYTES);
      const upload = await storageService.uploadResponseFile(link.id, field.id, preparedFile);
      preparedAnswers[field.id] = {
        name: upload.name,
        downloadUrl: upload.downloadUrl,
        path: upload.path,
        contentType: upload.contentType,
        size: upload.size,
      };
    }

    return preparedAnswers;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setStatus('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      const preparedAnswers = await prepareFileAnswers();
      await formService.submitResponse(
        { ...link, form },
        preparedAnswers,
        { asset, form },
      );
      setAnswers({});
      setStatus('Response submitted.');
    } catch (submitError) {
      setError(submitError.message || 'Unable to submit this response.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: `response-${field.id}`,
      required: Boolean(field.required),
    };
    const inputTypeByFieldType = {
      email: 'email',
      tel: 'tel',
      date: 'date',
      time: 'time',
      number: 'number',
      text: 'text',
    };

    if (field.type === 'textarea') {
      return (
        <textarea
          {...commonProps}
          className="field min-h-28 resize-none"
          onChange={(event) => updateAnswer(field.id, event.target.value)}
          placeholder={field.placeholder || ''}
          value={answers[field.id] || ''}
        />
      );
    }

    if (field.type === 'select') {
      const options = resolveFieldOptions(field, link.assets || []);
      return (
        <select
          {...commonProps}
          className="field"
          onChange={(event) => updateAnswer(field.id, event.target.value)}
          value={answers[field.id] || ''}
        >
          <option value="">Select an option</option>
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'multiselect') {
      const options = resolveFieldOptions(field, link.assets || []);
      const selectedValues = Array.isArray(answers[field.id]) ? answers[field.id] : [];
      return (
        <select
          {...commonProps}
          className="field min-h-32"
          multiple
          onChange={(event) => updateAnswer(
            field.id,
            Array.from(event.target.selectedOptions).map((option) => option.value),
          )}
          value={selectedValues}
        >
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label className="flex items-start gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-700">
          <input
            checked={Boolean(answers[field.id])}
            onChange={(event) => updateAnswer(field.id, event.target.checked)}
            type="checkbox"
          />
          <span>{field.placeholder || field.label}</span>
        </label>
      );
    }

    if (field.type === 'button') {
      const buttonUrl = normaliseUrl(field.buttonUrl || field.placeholder || '');
      return (
        <a
          className={`inline-flex rounded border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 ${buttonUrl ? '' : 'pointer-events-none opacity-50'}`}
          href={buttonUrl || '#'}
          rel="noreferrer"
          target="_blank"
        >
          {field.label || 'Open'}
        </a>
      );
    }

    if (field.type === 'file') {
      const answer = answers[field.id];
      return (
        <div>
          <input
            {...commonProps}
            className="field"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              if (file.size > MAX_RESPONSE_FILE_BYTES) {
                setError(`${field.label} must be ${formatFileSize(MAX_RESPONSE_FILE_BYTES)} or smaller.`);
                return;
              }
              setError('');
              updateAnswer(field.id, { file, name: file.name, size: file.size, contentType: file.type });
            }}
            type="file"
          />
          <p className="mt-1 text-xs text-slate-500">
            Max {formatFileSize(MAX_RESPONSE_FILE_BYTES)}. Images are converted to JPG and compressed to about 300 KB.
          </p>
          {answer?.name && (
            <p className="mt-2 text-xs font-semibold text-slate-600">
              Selected: {answer.name} ({formatFileSize(answer.size)})
            </p>
          )}
        </div>
      );
    }

    return (
      <input
        {...commonProps}
        className="field"
        inputMode={field.type === 'tel' ? 'tel' : field.type === 'number' ? 'decimal' : undefined}
        onChange={(event) => updateAnswer(field.id, event.target.value)}
        placeholder={field.placeholder || ''}
        type={inputTypeByFieldType[field.type] || 'text'}
        value={answers[field.id] || ''}
      />
    );
  };

  return (
    <section className={`${className} rounded-lg border border-slate-200 bg-white p-6`}>
      <div className="mb-5">
        <p className="label-caps">{asset ? 'Form' : 'Response form'}</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal">{form.title}</h2>
        {form.description && (
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">{form.description}</p>
        )}
      </div>

      <form className="grid gap-4" onSubmit={handleSubmit}>
        {fields.map((field) => (
          <div key={field.id}>
            {!['checkbox', 'button'].includes(field.type) && (
              <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor={`response-${field.id}`}>
                {field.label}
                {field.required && <span className="text-red-600"> *</span>}
              </label>
            )}
            {renderField(field)}
          </div>
        ))}

        {error && <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        {status && <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{status}</p>}

        <button
          className="justify-self-start rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
          disabled={isSubmitting}
          type="submit"
        >
          {isSubmitting ? 'Submitting...' : form.submitLabel || 'Submit response'}
        </button>
      </form>
    </section>
  );
}

export default ShareFormRenderer;
